/* SIZESOFTX
=========================================================================================
* Descripción: Componente de entrenamiento de IA para Celular - Responsive
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      2        | 25/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      3        | 11/05/2026 |                                  | Mejora responsive móvil
*      4        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Validación y permisos de cámara Android
*      5        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Selección por foto con flujo completo IA
*      6        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Normalización de métricas de aprendizaje móvil
*      7        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Selección de objeto y entrenamiento por muestras
*      8        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Encuadre de objeto por arrastre para entrenamiento IA
*      9        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Seguimiento local de objeto sin dependencias externas
*      10       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Envoltura por máscara local de canvas
*      11       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Envoltura asistida completa sin máscara por color
*      12       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Envoltura manual por lazo sin modelos externos
*      13       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Lazo libre sin fallback rectangular
*      14       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Rayos Fibonacci multicolor para diagnóstico visual
*      15       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Vista interna de grises y bordes para diagnóstico IA
*      16       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Huella radial RGB y gris desde el centro del lazo
*      17       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Optimización de fluidez del seguimiento radial
*      18       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Corrección de escala del lazo sobre el área visible del video
*      19       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Monitoreo visual de predicción y conteo de muestras
*      20       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Entrenamiento obligado con recorte activo del lazo de seguimiento
*      21       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Se ocultan rayos durante el trazo y se muestran al cerrar el lazo
*      22       | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Entrenamiento móvil usa recorte normalizado para cámaras distintas
*      23       | 19/07/2026 | Lic. José Luis Aragón Cervantes  | Fallback compatible para activar cámara móvil frontal y trasera
* ---------------------------------------------------------------------------------------
*/

import { Fragment, useEffect, useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card, CardContent, Typography, LinearProgress, Chip, CircularProgress, Stack, Grid } from "@mui/material";
import VideocamIcon from '@mui/icons-material/Videocam';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PsychologyIcon from '@mui/icons-material/Psychology';

import SizeSoftXLayout from "../../leyout/SizeSoftXLayout.jsx";
import { useIAEntrenamiento } from "../../../context/IA/IAEntrenamientoContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useFormTabLock } from "../../../hooks/useFormTabLock";
import ModalBuscarArticulosSimple from "../../herramientas/ModalBuscarArticulosSimple";
import ModalBuscarPorFoto from "../EntrenamientoPC/ModalBuscarPorFoto";
import MnuIAEntrenamientoActions from "../MnuIAEntrenamientoActions";
import CameraObjectSelectorOverlay from "../CameraObjectSelectorOverlay";
import {
    captureVideoFrame,
    createObjectSelectionFromLasso,
    createSelectionDebugPreview,
    createTrackingTemplate,
    getCameraPoint,
    trackObjectSelection,
} from "../utils/cameraSelection";

export default function FrmEntrenamientoIAMovil() {
    const navigate = useNavigate();
    const { isLocked: isFormLocked } = useFormTabLock('entrenamiento_ia_movil', {
        title: 'Entrenamiento IA Móvil',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Entrenamiento IA Móvil'} ya está abierto en otra pestaña`);
        }
    });

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const trackingAnimationRef = useRef(null);
    const trackingTemplateRef = useRef(null);
    const objectSelectionRef = useRef(null);
    const selectionAnchorRef = useRef(null);
    const lassoPointsRef = useRef([]);
    const predictionInFlightRef = useRef(false);
    const [useFrontCamera, setUseFrontCamera] = useState(false);
    const [cameraStatus, setCameraStatus] = useState("idle");
    const [cameraError, setCameraError] = useState("");
    const userActivatedCameraRef = useRef(false);

    const {
        articuloSeleccionado,
        setArticuloSeleccionado,
        imagenCapturada,
        setImagenCapturada,
        handleGuardarImagenArticulo,
        handleEnviarVectorEntrenamiento,
        handleObtenerInfoAprendizaje,
        handleEliminarEntrenamiento,
        handlePredecir,
        infoAprendizaje,
        imagenArticulo,
        statusBuscarArticuloSimple,
        handleAbrirBuscarArticulo,
        handleCerrarBuscarArticulo,
        handleArticuloSelect,
        medidaSeleccionada,
        setMedidaSeleccionada,
        getMedidasArticulo,
        metricasIA,
        handleObtenerMetricas,
        entrenamientoRed,
        handleEntrenarModelo,
    } = useIAEntrenamiento();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleAbrirBuscarArticuloRef = useRef(handleAbrirBuscarArticulo);
    useEffect(() => {
        handleAbrirBuscarArticuloRef.current = handleAbrirBuscarArticulo;
    }, [handleAbrirBuscarArticulo]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.altKey && event.key.trim().toUpperCase() === 'F1') {
                event.preventDefault();
                event.stopPropagation();
                handleAbrirBuscarArticuloRef.current();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const { handleChangeTitle, handleChangeClavePaginaActual, handleOpenAlert } = useLayout();

    useEffect(() => {
        if (isFormLocked) {
            alert("El formulario de Entrenamiento IA Móvil ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
            setTimeout(() => {
                navigate("/*");
            }, 900);
        }
    }, [isFormLocked]);

    const [loadingAprendizaje, setLoadingAprendizaje] = useState(false);
    const [openBusquedaFoto, setOpenBusquedaFoto] = useState(false);
    const [objectSelection, setObjectSelection] = useState(null);
    const [selectionAnchor, setSelectionAnchor] = useState(null);
    const [selectionDebugPreview, setSelectionDebugPreview] = useState(null);
    const [livePrediction, setLivePrediction] = useState(null);
    const [sampleProgress, setSampleProgress] = useState({ active: false, current: 0, total: 10 });

    useEffect(() => {
        objectSelectionRef.current = objectSelection;
    }, [objectSelection]);

    useEffect(() => {
        handleChangeTitle("Entrenamiento IA - Móvil");
        handleChangeClavePaginaActual("IAEntrenamientoMovil");
        
        return () => {
            detenerCamara();
            if (trackingAnimationRef.current) {
                cancelAnimationFrame(trackingAnimationRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let lastTracking = 0;

        const ejecutarSeguimiento = (timestamp) => {
            if (
                timestamp - lastTracking > 90 &&
                cameraStatus === "ready" &&
                trackingTemplateRef.current &&
                objectSelectionRef.current &&
                !selectionAnchorRef.current &&
                !imagenCapturada
            ) {
                const nextSelection = trackObjectSelection({
                    videoElement: videoRef.current,
                    currentSelection: objectSelectionRef.current,
                    template: trackingTemplateRef.current,
                    mirror: useFrontCamera,
                    searchRadiusPct: 0.045,
                    searchSteps: 3,
                });

                objectSelectionRef.current = nextSelection;
                setObjectSelection(nextSelection);
                lastTracking = timestamp;
            }

            trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);
        };

        trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);

        return () => {
            if (trackingAnimationRef.current) {
                cancelAnimationFrame(trackingAnimationRef.current);
            }
        };
    }, [cameraStatus, useFrontCamera, imagenCapturada]);

    useEffect(() => {
        if (articuloSeleccionado?.clavearticulo) {
            handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
            const medidas = getMedidasArticulo(articuloSeleccionado);
            if (medidas.length > 0 && !medidas.includes(medidaSeleccionada)) {
                setMedidaSeleccionada(medidas[0]);
            }
        }
    }, [articuloSeleccionado?.clavearticulo]);

    useEffect(() => {
        handleObtenerMetricas("articulos");
    }, []);

    useEffect(() => {
        if (!objectSelection || cameraStatus !== "ready" || imagenCapturada) {
            setSelectionDebugPreview(null);
            return undefined;
        }

        const interval = setInterval(() => {
            setSelectionDebugPreview(createSelectionDebugPreview({
                videoElement: videoRef.current,
                selection: objectSelectionRef.current,
                mirror: useFrontCamera,
                size: 80,
            }));
        }, 280);

        return () => clearInterval(interval);
    }, [Boolean(objectSelection), cameraStatus, imagenCapturada, useFrontCamera]);

    useEffect(() => {
        if (!objectSelection || !articuloSeleccionado || cameraStatus !== "ready" || imagenCapturada || sampleProgress.active) {
            setLivePrediction(null);
            return undefined;
        }

        const interval = setInterval(async () => {
            if (predictionInFlightRef.current || !objectSelectionRef.current) return;
            predictionInFlightRef.current = true;
            try {
                const imagenBase64 = captureVideoFrame({
                    videoElement: videoRef.current,
                    canvasElement: canvasRef.current,
                    selection: getSelectionForCapture(),
                    mirror: useFrontCamera,
                    quality: 0.68,
                });
                if (imagenBase64) {
                    const prediction = await handlePredecir(imagenBase64, { silent: true, minConfidence: 0.90, medida: medidaSeleccionada });
                    setLivePrediction(prediction);
                }
            } finally {
                predictionInFlightRef.current = false;
            }
        }, 3200);

        return () => clearInterval(interval);
    }, [Boolean(objectSelection), articuloSeleccionado?.clavearticulo, cameraStatus, imagenCapturada, sampleProgress.active, useFrontCamera]);

    const detenerCamara = () => {
        const activeStream = streamRef.current || videoRef.current?.srcObject;
        if (activeStream) {
            activeStream.getTracks().forEach(track => track.stop());
        }
        streamRef.current = null;
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const isCameraApiAvailable = () => Boolean(
        navigator.mediaDevices?.getUserMedia ||
        navigator.getUserMedia ||
        navigator.webkitGetUserMedia ||
        navigator.mozGetUserMedia ||
        navigator.msGetUserMedia
    );

    const getUserMediaCompatible = (constraints) => {
        if (navigator.mediaDevices?.getUserMedia) {
            return navigator.mediaDevices.getUserMedia(constraints);
        }

        const legacyGetUserMedia = navigator.getUserMedia ||
            navigator.webkitGetUserMedia ||
            navigator.mozGetUserMedia ||
            navigator.msGetUserMedia;

        if (legacyGetUserMedia) {
            return new Promise((resolve, reject) => {
                legacyGetUserMedia.call(navigator, constraints, resolve, reject);
            });
        }

        const error = new Error("CAMERA_API_UNAVAILABLE");
        error.name = "CameraApiUnavailableError";
        return Promise.reject(error);
    };

    const getCameraErrorMessage = (error) => {
        if (!window.isSecureContext) {
            return "La cámara en Android requiere abrir SizeSoftX desde HTTPS o localhost.";
        }
        if (!isCameraApiAvailable() || error?.name === "CameraApiUnavailableError") {
            return "Brave no expuso la camara para este sitio. Revise permisos de camara, configuracion del sitio y protecciones de Brave.";
        }
        if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
            return "Permiso de camara denegado. Active el permiso de camara del navegador para SizeSoftX.";
        }
        if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") {
            return "No se encontro una camara disponible en el dispositivo.";
        }
        if (error?.name === "NotReadableError" || error?.name === "TrackStartError") {
            return "La camara esta ocupada por otra aplicacion o pestana.";
        }
        if (error?.name === "OverconstrainedError") {
            return "La camara solicitada no esta disponible. Intente cambiar de camara.";
        }
        return `No se pudo iniciar la camara. ${error?.name ? `Detalle: ${error.name}` : "Revise permisos del navegador."}`;
    };

    const solicitarStreamCamara = async () => {
        const facingMode = useFrontCamera ? "user" : "environment";
        const intentos = [
            {
                video: {
                    facingMode: { ideal: facingMode },
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                },
            },
            {
                video: {
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                },
            },
            { video: true },
        ];

        let ultimoError = null;
        for (const constraints of intentos) {
            try {
                return await getUserMediaCompatible(constraints);
            } catch (error) {
                ultimoError = error;
                if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
                    throw error;
                }
            }
        }
        throw ultimoError || new Error("CAMERA_UNAVAILABLE");
    };

    const iniciarCamara = async () => {
        try {
            setCameraStatus("requesting");
            setCameraError("");

            if (!window.isSecureContext) {
                throw new Error("CAMERA_INSECURE_CONTEXT");
            }

            if (!isCameraApiAvailable()) {
                const error = new Error("CAMERA_API_UNAVAILABLE");
                error.name = "CameraApiUnavailableError";
                throw error;
            }

            detenerCamara();
            const mediaStream = await solicitarStreamCamara();
            streamRef.current = mediaStream;
            if (videoRef.current) {
                videoRef.current.setAttribute("playsinline", "true");
                videoRef.current.muted = true;
                videoRef.current.srcObject = mediaStream;
                await videoRef.current.play().catch(() => {});
            }
            setCameraStatus("ready");
            userActivatedCameraRef.current = true;
        } catch (err) {
            console.error("Error al iniciar cámara:", err);
            detenerCamara();
            setCameraStatus("error");
            setCameraError(getCameraErrorMessage(err));
        }
    };

    useEffect(() => {
        trackingTemplateRef.current = null;
        objectSelectionRef.current = null;
        setObjectSelection(null);
        if (userActivatedCameraRef.current) {
            iniciarCamara();
        }
    }, [useFrontCamera]);

    const handleArticuloFromFoto = async (articulo, imagen) => {
        await handleArticuloSelect(articulo);
        if (imagen) {
            setImagenCapturada(imagen);
        }
    };

    const handleSeleccionObjetoStart = (event) => {
        if (!videoRef.current || cameraStatus !== "ready") return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        const anchor = getCameraPoint(event, videoRef.current);
        lassoPointsRef.current = [anchor];
        selectionAnchorRef.current = anchor;
        trackingTemplateRef.current = null;
        const initialSelection = {
            xPct: anchor.xPct,
            yPct: anchor.yPct,
            widthPct: 0,
            heightPct: 0,
            contourPoints: [anchor],
            isDrawing: true,
        };
        objectSelectionRef.current = initialSelection;
        setSelectionAnchor(anchor);
        setObjectSelection(initialSelection);
    };

    const handleSeleccionObjetoMove = (event) => {
        const anchor = selectionAnchorRef.current || selectionAnchor;
        if (!videoRef.current || cameraStatus !== "ready" || !anchor) return;
        event.preventDefault();
        const endPoint = getCameraPoint(event, videoRef.current);
        const lastPoint = lassoPointsRef.current[lassoPointsRef.current.length - 1];
        const deltaX = Math.abs((lastPoint?.xPct ?? 0) - endPoint.xPct);
        const deltaY = Math.abs((lastPoint?.yPct ?? 0) - endPoint.yPct);

        if (deltaX > 0.006 || deltaY > 0.006) {
            lassoPointsRef.current = [...lassoPointsRef.current, endPoint];
        }

        const nextSelection = {
            ...createObjectSelectionFromLasso(lassoPointsRef.current, 0.08, true),
        };
        objectSelectionRef.current = nextSelection;
        setObjectSelection(nextSelection);
    };

    const handleSeleccionObjetoEnd = (event) => {
        const anchor = selectionAnchorRef.current || selectionAnchor;
        if (!anchor || !videoRef.current) return;
        event.preventDefault();
        const endPoint = getCameraPoint(event, videoRef.current);
        lassoPointsRef.current = [...lassoPointsRef.current, endPoint];
        const maskSelection = lassoPointsRef.current.length > 4
            ? createObjectSelectionFromLasso(lassoPointsRef.current, 0.08, false)
            : null;
        const trackingTemplate = maskSelection
            ? createTrackingTemplate({
                videoElement: videoRef.current,
                selection: maskSelection,
                mirror: useFrontCamera,
            })
            : null;
        const finalSelection = maskSelection && trackingTemplate?.radialDebugRays
            ? { ...maskSelection, debugRays: trackingTemplate.radialDebugRays }
            : maskSelection;
        objectSelectionRef.current = finalSelection;
        setObjectSelection(finalSelection);
        trackingTemplateRef.current = trackingTemplate;
        selectionAnchorRef.current = null;
        setSelectionAnchor(null);
        event.currentTarget.releasePointerCapture?.(event.pointerId);
    };

    const getSelectionForCapture = () => objectSelectionRef.current;

    const getSelectionForTraining = () => {
        const selection = getSelectionForCapture();
        if (!selection || selection.isDrawing || selection.widthPct <= 0 || selection.heightPct <= 0) {
            return null;
        }

        return selection;
    };

    const capturarImagenBase64 = (quality = 0.82) => {
        if (cameraStatus !== "ready") return null;
        const selection = getSelectionForTraining();
        if (!selection) {
            handleOpenAlert("Encierre el artículo con el lazo antes de capturar o entrenar");
            return null;
        }

        return captureVideoFrame({
            videoElement: videoRef.current,
            canvasElement: canvasRef.current,
            selection,
            mirror: useFrontCamera,
            quality,
        });
    };

    const capturarImagenActual = ({ guardar = true } = {}) => {
        const imagenBase64 = capturarImagenBase64(0.82);

        if (!imagenBase64) return null;

        setImagenCapturada(imagenBase64);

        if (guardar && articuloSeleccionado) {
            handleGuardarImagenArticulo(imagenBase64);
        }

        return imagenBase64;
    };

    const tomarFoto = () => capturarImagenActual({ guardar: true });

    const handleAsignarFoto = async () => {
        if (!articuloSeleccionado?.clavearticulo || loadingAprendizaje) return;
        const selection = getSelectionForTraining();
        if (selection) {
            tomarFoto();
        } else if (imagenCapturada) {
            const img = imagenCapturada;
            setImagenCapturada(null);
            await handleGuardarImagenArticulo(img);
        } else {
            handleOpenAlert("Seleccione el artículo en la cámara o busque por foto");
        }
    };

    const handleRefreshInfo = async () => {
        if (articuloSeleccionado?.clavearticulo) {
            setLoadingAprendizaje(true);
            await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
            setLoadingAprendizaje(false);
        }
    };

    const handleEnviarEntrenamiento = async () => {
        if (!articuloSeleccionado || cameraStatus !== "ready") return;

        const imagenBase64 = capturarImagenBase64(0.82);
        if (!imagenBase64) return;

        setLoadingAprendizaje(true);
        await handleEnviarVectorEntrenamiento(imagenBase64, null, { medida: medidaSeleccionada });
        await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        setLoadingAprendizaje(false);
    };

    const handleEntrenarRed = async () => {
        await handleEntrenarModelo("articulos", 8);
    };

    const esperar = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const getPredictionClave = (prediction) => (
        prediction?.clavearticulo
        || prediction?.claveArticulo
        || prediction?.sClaveArticulo
        || prediction?.SClaveArticulo
        || ""
    ).trim();

    const getPredictionDescripcion = (prediction) => (
        prediction?.descripcion
        || prediction?.descripcionarticulo
        || prediction?.descripcionArticulo
        || prediction?.sDescripcion
        || prediction?.mensaje
        || ""
    ).trim();

    const handleEntrenarMuestras = async () => {
        if (!articuloSeleccionado || cameraStatus !== "ready" || sampleProgress.active) return;

        setLoadingAprendizaje(true);
        setSampleProgress({ active: true, current: 0, total: 10 });

        for (let muestra = 1; muestra <= 10; muestra += 1) {
            setSampleProgress({ active: true, current: muestra, total: 10 });
            const imagenBase64 = capturarImagenBase64(0.82);
            if (imagenBase64) {
                await handleEnviarVectorEntrenamiento(imagenBase64, null, { medida: medidaSeleccionada, silent: muestra > 1 });
            }
            if (muestra < 5) {
                await esperar(1000);
            }
        }

        await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        setSampleProgress({ active: false, current: 0, total: 10 });
        setLoadingAprendizaje(false);
    };

    const handleEliminarTrainings = async () => {
        setLivePrediction(null);
        await handleEliminarEntrenamiento();
        if (articuloSeleccionado?.clavearticulo) {
            await handleObtenerInfoAprendizaje(articuloSeleccionado.clavearticulo);
        }
        setLivePrediction(null);
    };

    const infoArticuloActual = useMemo(() => {
        if (!articuloSeleccionado || !infoAprendizaje.length) return null;
        
        const clave = (articuloSeleccionado.clavearticulo || '').trim();
        const found = infoAprendizaje.find(i => {
            const claveInfo = (i.clavearticulo || i.sClaveArticulo || '').trim().toUpperCase();
            return claveInfo === clave.toUpperCase();
        });
        
        return found || null;
    }, [infoAprendizaje, articuloSeleccionado]);

    const porcentaje = Number(infoArticuloActual?.porcentaje ?? infoArticuloActual?.dPorcentaje ?? 0);
    const vectores = Number(infoArticuloActual?.totalvectores ?? infoArticuloActual?.totalVectores ?? infoArticuloActual?.iTotalVectores ?? 0);
    const vectoresV2 = Number(infoArticuloActual?.vectoresv2 ?? infoArticuloActual?.vectoresV2 ?? infoArticuloActual?.iVectoresV2 ?? 0);
    const medidasArticulo = useMemo(
        () => getMedidasArticulo(articuloSeleccionado),
        [articuloSeleccionado]
    );
    const medidasConteo = useMemo(() => {
        const mapa = {};
        for (const m of (infoArticuloActual?.medidas || [])) {
            if (m?.medida) mapa[String(m.medida).toUpperCase()] = Number(m.vectores || 0);
        }
        return mapa;
    }, [infoArticuloActual]);
    const avanceRed = entrenamientoRed.totales > 0
        ? Math.round((entrenamientoRed.procesadas / entrenamientoRed.totales) * 100)
        : 0;

    const labelObjetoIA = useMemo(() => {
        const clave = getPredictionClave(livePrediction);
        const descripcion = getPredictionDescripcion(livePrediction);
        const confianza = livePrediction?.confianza !== undefined
            ? ` ${Math.round(Number(livePrediction.confianza) * 100)}%`
            : "";

        if (clave || descripcion) {
            return `${clave || "IA"}${confianza}${descripcion ? ` - ${descripcion}` : ""}`;
        }

        return articuloSeleccionado?.clavearticulo
            ? `${articuloSeleccionado.clavearticulo?.trim()} - monitoreando`
            : "Objeto IA";
    }, [livePrediction, articuloSeleccionado?.clavearticulo]);

    const getStatusColor = (p) => {
        if (p >= 100) return 'success';
        if (p >= 50) return 'info';
        if (p > 0) return 'warning';
        return 'default';
    };

    return (
        <Fragment>
            <SizeSoftXLayout 
                titlePage="Entrenamiento IA - Móvil"
                buttonActions={
                    <MnuIAEntrenamientoActions
                        onAbrirBusquedaFoto={() => setOpenBusquedaFoto(true)}
                        onEntrenar={handleEnviarEntrenamiento}
                        onEntrenarMuestras={handleEntrenarMuestras}
                        onEntrenarModelo={handleEntrenarRed}
                        onEliminarEntrenamiento={handleEliminarTrainings}
                        onCambiarCamara={() => setUseFrontCamera(!useFrontCamera)}
                        entrenamientoActivo={sampleProgress.active || entrenamientoRed.activo}
                        disabledEntrenar={!articuloSeleccionado || !objectSelection}
                        disabledDelete={!articuloSeleccionado?.clavearticulo}
                    />
                }
            >
                <ModalBuscarArticulosSimple
                    openModal={statusBuscarArticuloSimple}
                    handleCloseModal={handleCerrarBuscarArticulo}
                    handleArticuloSelect={handleArticuloSelect}
                />

                <ModalBuscarPorFoto
                    open={openBusquedaFoto}
                    onClose={() => setOpenBusquedaFoto(false)}
                    onArticuloSelect={handleArticuloFromFoto}
                    useFrontCamera={useFrontCamera}
                />
                
                <Box sx={{ 
                    p: { xs: 1, sm: 2 },
                    pb: { xs: 12, sm: 12 }
                }}>
                    <Card sx={{ mb: 2 }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <VideocamIcon color="primary" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Cámara ({useFrontCamera ? 'Frontal' : 'Trasera'})
                                </Typography>
                            </Box>
                            
                            <Box sx={{ 
                                position: 'relative', 
                                bgcolor: '#000', 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                aspectRatio: '4/3',
                                cursor: 'crosshair',
                                touchAction: 'none',
                            }}
                            onPointerDown={handleSeleccionObjetoStart}
                            onPointerMove={handleSeleccionObjetoMove}
                            onPointerUp={handleSeleccionObjetoEnd}
                            onPointerCancel={handleSeleccionObjetoEnd}
                            >
                                <video 
                                    ref={videoRef} 
                                    autoPlay 
                                    playsInline 
                                    muted
                                    style={{ 
                                        width: '100%', 
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: imagenCapturada ? 'none' : 'block',
                                        transform: useFrontCamera ? 'scaleX(-1)' : 'none'
                                    }}
                                />

                                <CameraObjectSelectorOverlay selection={objectSelection} disabled={cameraStatus !== "ready" || Boolean(imagenCapturada)} videoElement={videoRef.current} label={labelObjetoIA} />

                                {sampleProgress.active && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                                bgcolor: 'rgba(0,0,0,.72)',
                                                color: '#fff',
                                                fontWeight: 900,
                                                fontSize: 20,
                                                boxShadow: 4,
                                                border: '2px solid #ffd600',
                                            }}
                                        >
                                            Muestra {sampleProgress.current} / {sampleProgress.total}
                                        </Box>
                                    </Box>
                                )}

                                {selectionDebugPreview && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 6,
                                            bottom: 6,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            p: 0.6,
                                            borderRadius: 1,
                                            bgcolor: 'rgba(0,0,0,.72)',
                                            color: '#fff',
                                            pointerEvents: 'none',
                                            boxShadow: 3,
                                        }}
                                    >
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, lineHeight: 1 }}>
                                                Gris
                                            </Typography>
                                            <img src={selectionDebugPreview.grayDataUrl} alt="Diagnóstico gris" width="58" height="58" style={{ objectFit: 'cover', border: '1px solid #90caf9' }} />
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, lineHeight: 1 }}>
                                                Bordes
                                            </Typography>
                                            <img src={selectionDebugPreview.edgeDataUrl} alt="Diagnóstico bordes" width="58" height="58" style={{ objectFit: 'cover', border: '1px solid #ffeb3b' }} />
                                        </Box>
                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 54, fontSize: 10 }}>
                                            C {Math.round(selectionDebugPreview.centerXPct * 100)} / {Math.round(selectionDebugPreview.centerYPct * 100)}
                                            <br />
                                            B {selectionDebugPreview.edgeWeight}
                                        </Typography>
                                    </Box>
                                )}

                                {cameraStatus !== "ready" && !imagenCapturada && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexDirection: "column",
                                            gap: 1,
                                            p: 2,
                                            color: "white",
                                            textAlign: "center",
                                            bgcolor: "rgba(0,0,0,.72)",
                                        }}
                                    >
                                        {cameraStatus === "requesting" ? (
                                            <>
                                                <CircularProgress size={28} sx={{ color: "white" }} />
                                                <Typography variant="body2">
                                                    Solicitando permiso de cámara...
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    Cámara no activa
                                                </Typography>
                                                {cameraError && (
                                                    <Typography variant="caption">
                                                        {cameraError}
                                                    </Typography>
                                                )}
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<VideocamIcon />}
                                                    onClick={iniciarCamara}
                                                    disabled={cameraStatus === "requesting"}
                                                >
                                                    Activar cámara
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                )}
                                
                                {imagenCapturada && (
                                    <Box sx={{ position: 'relative' }}>
                                        <img 
                                            src={imagenCapturada} 
                                            alt="Capturada" 
                                            style={{ 
                                                width: '100%', 
                                                height: '100%',
                                                objectFit: 'contain',
                                                background: '#000'
                                            }} 
                                        />
                                    </Box>
                                )}
                                
                                <canvas ref={canvasRef} style={{ display: 'none' }} />
                            </Box>
                        </CardContent>
                    </Card>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CameraAltIcon />}
                        onClick={handleAsignarFoto}
                        disabled={!articuloSeleccionado?.clavearticulo || loadingAprendizaje}
                        fullWidth
                        size="large"
                        sx={{ mb: 2 }}
                    >
                        Asignar foto al artículo
                    </Button>

                    {articuloSeleccionado && (
                        <Card sx={{ mb: 2 }}>
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                                    Artículo Seleccionado
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 1, sm: 2 },
                                    p: 1.5,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 1
                                }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Clave
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {articuloSeleccionado.clavearticulo}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Descripción
                                        </Typography>
                                        <Typography variant="body2">
                                            {articuloSeleccionado.descripcion}
                                        </Typography>
                                    </Box>
                                </Box>

                                {articuloSeleccionado.marca && (
                                    <Box sx={{ mt: 1, px: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Marca
                                        </Typography>
                                        <Typography variant="body2">
                                            {articuloSeleccionado.marca}
                                        </Typography>
                                    </Box>
                                )}

                                {medidasArticulo.length > 0 && (
                                    <Box sx={{ mt: 1.5, px: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                            Medida en entrenamiento (3 base, mismo flujo)
                                        </Typography>
                                        <Stack direction="row" sx={{ mt: 0.75, flexWrap: 'wrap', gap: 1 }}>
                                            {medidasArticulo.map((med) => {
                                                const conteo = medidasConteo[String(med).toUpperCase()] ?? 0;
                                                const activa = String(medidaSeleccionada).toUpperCase() === String(med).toUpperCase();
                                                return (
                                                    <Chip
                                                        key={med}
                                                        label={`${med} (${conteo})`}
                                                        color={activa ? "primary" : "default"}
                                                        variant={activa ? "filled" : "outlined"}
                                                        size="small"
                                                        onClick={() => setMedidaSeleccionada(med)}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {articuloSeleccionado && (
                        <>
                            {imagenArticulo && (
                                <Card sx={{ mb: 2 }}>
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            Imagen del Artículo
                                        </Typography>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center',
                                            bgcolor: '#fafafa',
                                            borderRadius: 1,
                                            p: 1
                                        }}>
                                            <img 
                                                src={imagenArticulo} 
                                                alt="Artículo" 
                                                style={{ 
                                                    maxWidth: '100%',
                                                    maxHeight: 120,
                                                    objectFit: 'contain',
                                                    borderRadius: 4,
                                                    border: '2px solid #1976d2'
                                                }} 
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}

                    {articuloSeleccionado && (
                        <Card
                            sx={{
                                position: 'sticky',
                                bottom: 10,
                                zIndex: 20,
                                border: '1px solid #d7e3f5',
                                boxShadow: 5,
                            }}
                        >
                            <CardContent sx={{ py: 1.25 }}>
                                <Grid container spacing={1.5} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PsychologyIcon fontSize="small" color="primary" /> Aprendizaje
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Vectores: <strong>{vectores}</strong> (v2: <strong>{vectoresV2}</strong>)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Red: <strong>v{metricasIA?.version ?? 0}</strong>
                                            {metricasIA ? ` · ${metricasIA.ramas ?? 0} ramas · ${metricasIA.vectores ?? 0} muestras` : ""}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                            <Typography variant="caption">Progreso</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>{porcentaje}%</Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={porcentaje}
                                            color={getStatusColor(porcentaje)}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                        {sampleProgress.active && (
                                            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>
                                                Muestra {sampleProgress.current} de {sampleProgress.total}
                                                {medidaSeleccionada ? ` · medida ${medidaSeleccionada}` : ""}
                                            </Typography>
                                        )}
                                        {entrenamientoRed.activo && (
                                            <Box sx={{ mt: 0.5 }}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                        Entrenando red {entrenamientoRed.procesadas}/{entrenamientoRed.totales}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 800 }}>{avanceRed}%</Typography>
                                                </Stack>
                                                <LinearProgress variant="determinate" value={avanceRed} color="secondary" sx={{ height: 7, borderRadius: 4 }} />
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                                            {loadingAprendizaje && <CircularProgress size={18} />}
                                            <Chip
                                                icon={<PsychologyIcon />}
                                                label={porcentaje >= 100 ? "Optimizado" : "Requiere muestras"}
                                                color={getStatusColor(porcentaje)}
                                                size="small"
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </SizeSoftXLayout>
        </Fragment>
    );
}
